import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { api } from '../api/client';
import { toast } from '../stores/toasts';
import { Modal } from '../components/Modal';

const STATUS_LABELS: Record<string, string> = {
  PENDING: '⏳ Kutilmoqda',
  CONFIRMED: '✅ Tasdiqlangan',
  REJECTED: '❌ Rad etilgan',
  CANCELLED: '🚫 Bekor qilindi',
  COMPLETED: '🏁 Yakunlandi',
};

const STATUS_TAGS: Record<string, string> = {
  PENDING: 'warning',
  CONFIRMED: 'success',
  REJECTED: 'danger',
  CANCELLED: '',
  COMPLETED: 'primary',
};

export function MyBookingsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  const [cancelModal, setCancelModal] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['my-bookings'],
    queryFn: () => api.myBookings(),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => api.cancelBooking(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-bookings'] });
      toast.success('Band bekor qilindi');
      setCancelModal(null);
    },
    onError: (e: any) => toast.error(e?.message || 'Xatolik'),
  });

  const bookings = data ?? [];
  const now = new Date();
  const upcoming = bookings.filter(
    (b: any) => new Date(b.scheduledAt) > now && !['CANCELLED', 'COMPLETED', 'REJECTED'].includes(b.status),
  );
  const past = bookings.filter(
    (b: any) => new Date(b.scheduledAt) <= now || ['CANCELLED', 'COMPLETED', 'REJECTED'].includes(b.status),
  );
  const list = tab === 'upcoming' ? upcoming : past;

  return (
    <>
      <h3>📅 Mening bandlarim</h3>
      <div className="tab-bar">
        <div className={`tab ${tab === 'upcoming' ? 'active' : ''}`} onClick={() => setTab('upcoming')}>
          Yaqin ({upcoming.length})
        </div>
        <div className={`tab ${tab === 'past' ? 'active' : ''}`} onClick={() => setTab('past')}>
          O'tgan ({past.length})
        </div>
      </div>

      {isLoading ? (
        <div className="spinner" />
      ) : list.length > 0 ? (
        list.map((b: any) => (
          <div key={b.id} className="card">
            <div className="row-between" style={{ marginBottom: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{b.workshop.name}</div>
              <span className={`tag ${STATUS_TAGS[b.status]}`}>
                {STATUS_LABELS[b.status] ?? b.status}
              </span>
            </div>
            <div className="muted" style={{ fontSize: 13, marginBottom: 4 }}>
              {b.service?.name ?? 'Xizmat'}
            </div>
            <div className="row" style={{ gap: 6, fontSize: 13 }}>
              <span>🚗 {b.car?.plate ?? '—'}</span>
              <span className="muted">·</span>
              <span>🕐 {format(parseISO(b.scheduledAt), 'dd MMM, HH:mm')}</span>
            </div>
            {b.customerNote && (
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                💬 {b.customerNote}
              </div>
            )}
            {b.workshopNote && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4, padding: 6, background: 'var(--surface-2)', borderRadius: 6 }}>
                🏢 {b.workshopNote}
              </div>
            )}
            {tab === 'upcoming' && !['CANCELLED', 'REJECTED'].includes(b.status) && (
              <button
                className="btn btn-block btn-danger"
                style={{ marginTop: 10 }}
                onClick={() => setCancelModal(b.id)}
              >
                Bekor qilish
              </button>
            )}
          </div>
        ))
      ) : (
        <div className="empty">
          <div className="emoji">📅</div>
          <div>{tab === 'upcoming' ? 'Yaqin bandlar yo\'q' : 'O\'tgan bandlar yo\'q'}</div>
        </div>
      )}

      <Modal
        open={!!cancelModal}
        onClose={() => setCancelModal(null)}
        title="Bandni bekor qilish"
        footer={
          <>
            <button className="btn" onClick={() => setCancelModal(null)}>Yo'q</button>
            <button
              className="btn btn-danger"
              onClick={() => cancelModal && cancel.mutate(cancelModal)}
              disabled={cancel.isPending}
            >
              Ha, bekor qilish
            </button>
          </>
        }
      >
        <p>Bandni bekor qilmoqchimisiz?</p>
        <p className="muted" style={{ fontSize: 13 }}>Servis xabardor bo'ladi.</p>
      </Modal>
    </>
  );
}
